import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert, Autocomplete, Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Divider,
  FormControl, InputLabel, MenuItem, Select, Stack, TextField, Typography,
} from '@mui/material';
import { useSnackbar } from 'notistack';
import { api } from '../../services/api';
import { defaultStartLocal } from '../../utils/optimizeScheduleUtils';
import {
  DEFAULT_NEIGHBOR_SCAN_FORM, NEIGHBOR_SCAN_SELECTIONS, neighborScanFormFromRequest, neighborScanFormProblem,
  neighborScanFormToRequest, neighborScanScheduleDevices,
} from '../../utils/neighborScanScheduleUtils';
import ScheduleWhenFields, { scheduleWhenProblems, useSiteTimezones, useTimezoneOptions } from './ScheduleWhenFields';
import NeighborScanBatchField from '../NeighborScanBatchField';

const deviceLabel = (d) => (d?.name && d.name !== d.serial_number ? `${d.name} (${d.serial_number})` : d?.serial_number || '');

/**
 * Create or edit a scheduled neighbours scan: the same choices as the
 * Neighbours → Run Scan tab (which devices, seconds per channel, all or
 * known channels, devices scanning at once) at a set site time. Each run
 * appears in Neighbours → Recent Scans with its tracker.
 */
export default function NeighborScanScheduleDialog({ open, schedule, devices, onClose, onSaved }) {
  const { enqueueSnackbar } = useSnackbar();
  const editing = Boolean(schedule);
  const { timezones, loaded: tzLoaded, error: tzError } = useSiteTimezones(open);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(null);
  const { caps, nodes } = useMemo(() => neighborScanScheduleDevices(devices), [devices]);
  const all = useMemo(() => [...nodes, ...caps], [nodes, caps]);
  const bySerial = useMemo(() => new Map(all.map((d) => [d.serial_number, d])), [all]);

  useEffect(() => {
    if (!open) {
      setForm(null);
      return;
    }
    if (form) return;
    if (editing) {
      setForm({
        name: schedule.name || '',
        enabled: schedule.enabled !== false,
        timezone: schedule.timezone || '',
        startLocal: schedule.start_local || '',
        repeat: schedule.repeat || 'daily',
        scan: neighborScanFormFromRequest(schedule.neighbor_scan_request || {}),
      });
    } else if (tzLoaded) {
      const tz = timezones[0] || '';
      setForm({
        name: 'Nightly neighbours scan',
        enabled: true,
        timezone: tz,
        startLocal: defaultStartLocal(tz, 3),
        repeat: 'daily',
        scan: { ...DEFAULT_NEIGHBOR_SCAN_FORM },
      });
    }
  }, [open, form, editing, schedule, timezones, tzLoaded]);

  const tzOptions = useTimezoneOptions(timezones, form?.timezone);
  const set = (patch) => setForm((f) => ({ ...f, ...patch }));
  const setScan = (patch) => setForm((f) => ({ ...f, scan: { ...f.scan, ...patch } }));

  const problems = [];
  if (form) {
    problems.push(...scheduleWhenProblems(form));
    const p = neighborScanFormProblem(form.scan);
    if (p) problems.push(p);
  }
  const missing = form?.scan.selection === 'specific' && all.length > 0
    ? form.scan.serialNumbers.filter((sn) => !bySerial.has(sn))
    : [];

  const save = async () => {
    if (!form || problems.length > 0) return;
    setSaving(true);
    try {
      const body = {
        name: form.name.trim(),
        enabled: form.enabled,
        timezone: form.timezone,
        start_local: form.startLocal,
        repeat: form.repeat,
        neighbor_scan_request: neighborScanFormToRequest(form.scan),
      };
      const saved = editing
        ? await api.updateNeighborScanSchedule(schedule.id, body)
        : await api.createNeighborScanSchedule(body);
      enqueueSnackbar(editing ? 'Schedule updated.' : 'Schedule created.', { variant: 'success' });
      onSaved(saved);
    } catch (err) {
      enqueueSnackbar(err?.data?.error || err?.message || 'Failed to save the schedule', { variant: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const selectionCount = (sel) => {
    if (sel === 'all_caps') return caps.filter((d) => d.connected).length;
    if (sel === 'specific') return null;
    return nodes.filter((d) => d.connected).length;
  };

  return (
    <Dialog open={open} onClose={() => !saving && onClose()} fullWidth maxWidth="md">
      <DialogTitle>{editing ? 'Edit scheduled neighbours scan' : 'New scheduled neighbours scan'}</DialogTitle>
      <DialogContent dividers>
        {!form ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress size={28} /></Box>
        ) : (
          <Stack spacing={2.5}>
            <Typography variant="body2" color="text.secondary">
              Each run starts the same neighbours scan as Run Scan in the Neighbours tab, with the devices and settings
              below. The selection is resolved when the run starts (devices offline then are skipped). Each run appears
              in Neighbours → Recent Scans with its tracker. A run is skipped (and shown as skipped) while another
              scheduled run, an optimize, an iperf test or a plan is in progress for this site, or while a scan is
              already running. A scan in progress does not hold other scheduled runs.
            </Typography>
            {tzError && <Alert severity="error">{tzError}</Alert>}

            <ScheduleWhenFields form={form} onChange={set} tzOptions={tzOptions} editing={editing} />

            <Divider />

            <Box>
              <Typography variant="subtitle2" sx={{ mb: 1.5 }}>Devices to scan</Typography>
              <Stack spacing={2}>
                <FormControl size="small" fullWidth>
                  <InputLabel>Selection</InputLabel>
                  <Select
                    label="Selection"
                    value={form.scan.selection}
                    onChange={(e) => setScan({ selection: e.target.value })}
                  >
                    {NEIGHBOR_SCAN_SELECTIONS.map((o) => {
                      const n = selectionCount(o.value);
                      return (
                        <MenuItem key={o.value} value={o.value}>
                          {o.label}{n !== null && all.length > 0 ? ` (${n} now)` : ''}
                        </MenuItem>
                      );
                    })}
                  </Select>
                </FormControl>
                {form.scan.selection === 'specific' && (
                  <Autocomplete
                    multiple
                    size="small"
                    options={all.map((d) => d.serial_number)}
                    groupBy={(sn) => (bySerial.get(sn) && caps.includes(bySerial.get(sn)) ? 'CAPs' : 'Nodes')}
                    value={form.scan.serialNumbers}
                    onChange={(_e, v) => setScan({ serialNumbers: v })}
                    getOptionLabel={(sn) => (bySerial.has(sn) ? deviceLabel(bySerial.get(sn)) : sn)}
                    renderOption={(props, sn) => {
                      const d = bySerial.get(sn);
                      return (
                        <li {...props} key={sn}>
                          <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: d?.connected ? 'success.main' : 'error.main', mr: 1 }} />
                          {deviceLabel(d) || sn}
                        </li>
                      );
                    }}
                    renderTags={(value, getTagProps) => value.map((sn, index) => (
                      <Chip
                        {...getTagProps({ index })}
                        key={sn}
                        size="small"
                        label={bySerial.has(sn) ? deviceLabel(bySerial.get(sn)) : sn}
                        color={missing.includes(sn) ? 'error' : 'default'}
                        variant={missing.includes(sn) ? 'outlined' : 'filled'}
                      />
                    ))}
                    renderInput={(params) => <TextField {...params} label="Devices" placeholder={all.length ? 'Select devices' : 'No devices loaded'} />}
                  />
                )}
                {missing.length > 0 && (
                  <Alert severity="warning">
                    {missing.length} selected device{missing.length > 1 ? 's' : ''} not found on this site; the run scans only
                    the devices that exist and are online.
                  </Alert>
                )}
              </Stack>
            </Box>

            <Box>
              <Typography variant="subtitle2" sx={{ mb: 1.5 }}>Scan settings</Typography>
              <Stack spacing={2}>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                  <TextField
                    size="small"
                    type="number"
                    label="Scan interval (seconds per channel)"
                    value={form.scan.chanScanInterval}
                    onChange={(e) => setScan({ chanScanInterval: e.target.value === '' ? '' : Number(e.target.value) })}
                    inputProps={{ min: 1, max: 60 }}
                    sx={{ minWidth: 260 }}
                  />
                  <FormControl size="small" sx={{ minWidth: 220 }}>
                    <InputLabel>Channels to scan</InputLabel>
                    <Select label="Channels to scan" value={form.scan.allFreq} onChange={(e) => setScan({ allFreq: e.target.value })}>
                      <MenuItem value>All channels (all bands)</MenuItem>
                      <MenuItem value={false}>Known channels only</MenuItem>
                    </Select>
                  </FormControl>
                </Stack>
                <NeighborScanBatchField value={form.scan.batchSize} onChange={(v) => setScan({ batchSize: v })} />
              </Stack>
            </Box>
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        {form && problems.length > 0 && (
          <Typography variant="caption" color="error" sx={{ mr: 'auto', pl: 2 }}>{problems[0]}</Typography>
        )}
        <Button onClick={onClose} disabled={saving}>Cancel</Button>
        <Button
          variant="contained"
          onClick={save}
          disabled={!form || saving || problems.length > 0}
          startIcon={saving ? <CircularProgress size={16} color="inherit" /> : null}
        >
          {editing ? 'Save' : 'Create schedule'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
